import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import Title from '../../../components/Title';
import { NavLink, useLocation } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { useTheme, useMediaQuery, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, TextField } from '@mui/material';
import moment from 'moment-timezone';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSelection } from '../../../components/context/SelectionContext';
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import { useDrawer } from '../../../components/context/DrawerContext';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface ExpenseDetail {
    id: number;
    date: string;
    createdAt?: string | Date;
    description: string;
    value: number;
    balance: number;
    boxOrigin: {
        id: number;
        balance: number;
        date: string;
        collect: {
            id: number;
            name: string;
        } | null; // collect puede ser null
    } | null; // boxOrigin puede ser null
    boxDestination: {
        id: number;
        balance: number;
        date: string;
        collect: {
            id: number;
            name: string;
        } | null; // collect puede ser null
    } | null; // boxDestination puede ser null
    expense: {
        id: number;
        name: string;
    };
    type_operation: {
        id: number;
        name: string;
    };
    collectName: string;
}

const DetalleGastos: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = "Registrar Gastos";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();

    const [expensesDetail, setExpensesDetail] = useState<ExpenseDetail[]>([]);
    const { office_id, setOffice_id, collect_id, setCollect_id } = useSelection();
    const { drawerOpen } = useDrawer();
    const { canDeleteExpenseMovement } = useResourcePermissions('/prestamos/gastos/list');
    const todayOperational = nowOperationalDateString();
    const [startDate, setStartDate] = useState<string>(todayOperational);
    const [endDate, setEndDate] = useState<string>(todayOperational);
    const [opFilter, setOpFilter] = useState<'all' | 'sum' | 'rest'>('all');

    const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);

    // Cargar oficinas y datos relacionados con office_id

    useEffect(() => {
        const fetchOffice = async () => {
            try {
                showLoading();
                setExpensesDetail([]); // Limpiar los detalles si no hay collect_id
                const { data } = await axios.get('offices');
                setOffices(data.data);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                showAlert('Error consultando oficinas', 'error');
            } finally {
                hideLoading();
            }
        };
        fetchOffice();
    }, []); // Solo depende de office_id

    useEffect(() => {
        // Si office_id no tiene un valor válido, no ejecutar el efecto
        if (!office_id) {
            return;
        }

        const fetchDataDetailOffice = async () => {
            showLoading();
            try {
                setExpensesDetail([]); // Limpiar los detalles si no hay collect_id
                const officesResponse = axios.get('offices');
                const collectsOfficeResponse = office_id ? axios.get(`collectsoffice/${office_id}`) : null;
                const { data } = await axios.get(`expensesdetailoffice/${office_id}`, {
                    params: { start_date: todayOperational, end_date: todayOperational },
                });
                setExpensesDetail(Array.isArray(data) ? data : []);

                const [officesData, collectsOfficeData] = await Promise.all([
                    officesResponse,
                    collectsOfficeResponse,
                ]);

                if (officesData) setOffices(officesData.data.data);
                if (collectsOfficeData) setCollectsOffice(collectsOfficeData.data);

                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                showAlert('Error fetching data', 'error');
            } finally {
                hideLoading();
            }
        };

        fetchDataDetailOffice();
    }, [office_id, todayOperational]); // Solo depende de office_id
    const BoxOfficeMovemente = async () => {
        showLoading();
        try {
            setExpensesDetail([]); // Limpiar los detalles si no hay collect_id
            const officesResponse = axios.get('offices');
            const collectsOfficeResponse = office_id ? axios.get(`collectsoffice/${office_id}`) : null;
            const { data } = await axios.get(`expensesdetailoffice/${office_id}`, {
                params: { start_date: startDate, end_date: endDate },
            });
            setExpensesDetail(Array.isArray(data) ? data : []);

            const [officesData, collectsOfficeData] = await Promise.all([
                officesResponse,
                collectsOfficeResponse,
            ]);

            if (officesData) setOffices(officesData.data.data);
            if (collectsOfficeData) setCollectsOffice(collectsOfficeData.data);

            await new Promise((resolve) => setTimeout(resolve, 1000));
            showAlert('Se ha realizado la consulta exitosamente!', 'info');

        } catch (error) {
            showAlert('Error fetching data', 'error');
        } finally {
            hideLoading();
        }
    };

    const decimalFormatter = useMemo(() => new Intl.NumberFormat('en-US', { style: 'decimal' }), []);

    const formatDate = (value: string | number | undefined) => {
        if (!value) return '-';
        try {
            return format(parseISO(String(value)), 'yyyy-MM-dd HH:mm:ss');
        } catch (error) {
            return String(value);
        }
    };

    const formatDateTimeBogota = (row: SimpleRow) => {
        const raw = (row as any)?.createdAt || (row as any)?.created_at || row.date;
        if (!raw) return '-';
        const m = moment.tz(raw, 'America/Bogota');
        if (m.isValid()) {
            return m.format('YYYY-MM-DD HH:mm');
        }
        return formatDate(String(raw));
    };

    const isTodayOperational = (row: SimpleRow) => {
        const today = nowOperationalDateString(); // YYYY-MM-DD (Bogotá)
        const raw = (row as any)?.createdAt || (row as any)?.created_at || row.date;
        const m = moment.tz(raw, ['YYYY-MM-DD', moment.ISO_8601], 'America/Bogota');
        if (!m.isValid()) return false;
        return m.format('YYYY-MM-DD') === today;
    };

    const refreshList = async () => {
        // Esta vista depende de office_id; el filtro por cobro se hace en el front.
        if (!office_id) return;
        const { data } = await axios.get(`expensesdetailoffice/${office_id}`, {
            params: { start_date: startDate, end_date: endDate },
        });
        setExpensesDetail(Array.isArray(data) ? data : []);
    };

    const handleDeleteMovement = async (row: SimpleRow) => {
        if (!canDeleteExpenseMovement) {
            return;
        }
        const id = Number(row.id);
        if (!id || !Number.isFinite(id)) {
            showAlert('No se pudo identificar el movimiento a eliminar.', 'warning');
            return;
        }
        if (!isTodayOperational(row)) {
            showAlert('Solo se pueden eliminar movimientos del día.', 'warning');
            return;
        }
        const ok = window.confirm('¿Eliminar este movimiento? Esta acción no se puede deshacer.');
        if (!ok) return;
        showLoading();
        try {
            await axios.delete(`/boxmovement/${id}`);
            showAlert('Movimiento eliminado.', 'success');
            await refreshList();
        } catch (error) {
            console.error(error);
            const msg =
                (error as any)?.response?.data?.message ||
                'No fue posible eliminar el movimiento.';
            showAlert(msg, 'error');
        } finally {
            hideLoading();
        }
    };

    const fullColumns: SimpleColumn[] = [
        {
            field: 'date',
            headerName: 'Fecha',
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }: { row: SimpleRow }) => formatDateTimeBogota(row),
        },
        { field: 'expense_name', headerName: 'Detalle Gasto', flex: 1, minWidth: 180, wrapText: true },
        { field: 'type_operation_name', headerName: 'Operación', flex: 1, minWidth: 160, wrapText: true },
        { field: 'collectName', headerName: 'Cobro', flex: 1, minWidth: 160, wrapText: true },
        { field: 'description', headerName: 'Descripción', flex: 1, minWidth: 200, wrapText: true },
        {
            field: 'value',
            headerName: 'Valor',
            flex: 1,
            minWidth: 140,
            align: 'right',
            renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.value ?? 0)),
        },
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => (
                <PermissionButton
                    allowed={canDeleteExpenseMovement}
                    deniedTooltip="Sin permiso para eliminar movimiento gastos"
                    tooltip={isTodayOperational(row) ? 'Eliminar' : 'Solo se puede eliminar movimientos del día'}
                    variant="contained"
                    color="error"
                    sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
                    disabled={!isTodayOperational(row)}
                    onClick={() => void handleDeleteMovement(row)}
                >
                    <DeleteIcon fontSize="small" />
                </PermissionButton>
            ),
        },
    ];

    const mobileColumns: SimpleColumn[] = [
        { field: 'expense_name', headerName: 'Detalle Gasto', flex: 1, minWidth: 200, wrapText: true },
        { field: 'collectName', headerName: 'Cobro', flex: 1, minWidth: 150, wrapText: true },
        {
            field: 'value',
            headerName: 'Valor',
            flex: 1,
            minWidth: 140,
            align: 'right',
            renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.value ?? 0)),
        },
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => (
                <PermissionButton
                    allowed={canDeleteExpenseMovement}
                    deniedTooltip="Sin permiso para eliminar movimiento gastos"
                    tooltip={isTodayOperational(row) ? 'Eliminar' : 'Solo se puede eliminar movimientos del día'}
                    variant="contained"
                    color="error"
                    sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
                    disabled={!isTodayOperational(row)}
                    onClick={() => void handleDeleteMovement(row)}
                >
                    <DeleteIcon fontSize="small" />
                </PermissionButton>
            ),
        },
    ];

    const simpleColumns = isMobile ? mobileColumns : fullColumns;

    const rows: SimpleRow[] = expensesDetail.map((expenseDetail) => ({
        id: expenseDetail.id as RowId,
        expense_name: expenseDetail.expense.name,
        type_operation_id: expenseDetail.type_operation?.id,
        type_operation_name: expenseDetail.type_operation.name,
        collect_id: expenseDetail.boxOrigin?.collect?.id ?? expenseDetail.boxDestination?.collect?.id ?? null,
        collectName: expenseDetail.boxOrigin?.collect?.name || expenseDetail.boxDestination?.collect?.name || expenseDetail.collectName || '-',
        value: expenseDetail.value ?? 0,
        description: expenseDetail.description,
        date: expenseDetail.date,
        createdAt: (expenseDetail as any)?.createdAt || (expenseDetail as any)?.created_at || undefined,
    }));

    const filteredRows = useMemo(() => {
        let next = rows;
        // Filtro por cobro (cuando está seleccionado)
        const numericCollectId = Number(collect_id);
        if (collect_id && collect_id !== "Seleccionar Cobro" && Number.isFinite(numericCollectId) && numericCollectId > 0) {
            next = next.filter((r) => Number((r as any)?.collect_id) === numericCollectId);
        }
        // Filtro por tipo operación
        if (opFilter !== 'all') {
            const wantTypeOpId = opFilter === 'sum' ? 1 : 2;
            next = next.filter((r) => Number((r as any)?.type_operation_id) === wantTypeOpId);
        }
        return next;
    }, [rows, collect_id, opFilter]);

    // Ordenar del último al primero (fallback en front, por si el API no ordena)
    const sortedRows = useMemo(() => {
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const aRaw = (a as any)?.createdAt || (a as any)?.created_at || a.date || '';
            const bRaw = (b as any)?.createdAt || (b as any)?.created_at || b.date || '';
            const aT = moment.tz(aRaw, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601], 'America/Bogota').valueOf();
            const bT = moment.tz(bRaw, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601], 'America/Bogota').valueOf();
            if (Number.isFinite(aT) && Number.isFinite(bT) && aT !== bT) return bT - aT;
            return Number(b.id ?? 0) - Number(a.id ?? 0);
        });
        return copy;
    }, [filteredRows]);

    const fetchMovementsForRange = async (rangeStart: string, rangeEnd: string) => {
        if (!office_id) {
            showAlert('Seleccione una oficina.', 'warning');
            return;
        }
        showLoading();
        try {
            const { data } = await axios.get(`expensesdetailoffice/${office_id}`, {
                params: { start_date: rangeStart, end_date: rangeEnd },
            });
            const list = Array.isArray(data) ? data : [];
            setExpensesDetail(list);
            if (list.length === 0) {
                showAlert('No existen registros para el rango seleccionado.', 'info');
            }
        } catch (error) {
            console.error(error);
            const msg =
                (error as any)?.response?.data?.message ||
                'No fue posible consultar los movimientos.';
            showAlert(msg, 'error');
            setExpensesDetail([]);
        } finally {
            hideLoading();
        }
    };

    const handleApplyDateRange = async () => {
        if (!startDate || !endDate) {
            showAlert('Seleccione fecha inicio y fecha fin.', 'warning');
            return;
        }
        const startM = moment.tz(startDate, ['YYYY-MM-DD'], 'America/Bogota');
        const endM = moment.tz(endDate, ['YYYY-MM-DD'], 'America/Bogota');
        if (!startM.isValid() || !endM.isValid()) {
            showAlert('Rango de fechas inválido.', 'warning');
            return;
        }
        if (endM.isBefore(startM, 'day')) {
            showAlert('La fecha fin no puede ser menor que la fecha inicio.', 'warning');
            return;
        }
        await fetchMovementsForRange(startDate, endDate);
    };

    const addExpensePath = location.pathname.includes('/administrar/detallegasto')
        ? '/administrar/detallegasto/add'
        : '/prestamos/gastos/add';

    return (
        <HeaderResponsive >
            <StickyListToolbar>
                <Title>Información Registro Gastos</Title>
                <Box sx={{ ml: 'auto' }}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to={addExpensePath}
                    >
                        {isMobile ? <AddIcon /> : 'Agregar'}
                    </Button>
                </Box>
            </StickyListToolbar>
            <Card
                sx={{
                    width: (t) => stickyListRegionWidth(t, drawerOpen),
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'auto',
                }}
            >
                <form onSubmit={(e) => e.preventDefault()}>
                    <CardContent>
                         <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} xl={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="form-layouts-separator-select-label">
                                        Seleccionar Oficina
                                    </InputLabel>
                                    {offices && offices.length > 0 ? (
                                        <Select
                                            label="Seleccionar Oficina"
                                            value={office_id || ""} // Usa un valor por defecto (cadena vacía) si office_id es null o undefined
                                            labelId="office_id"
                                            id="office_id"
                                            size='small'
                                            onChange={(event) => setOffice_id(event.target.value)} // Asegúrate de que setOffice_id esté actualizando el estado correctamente
                                            required
                                        >
                                            {offices.map((office: Office) => (
                                                <MenuItem key={office.id} value={office.id}> {/* Asegúrate de que office.id sea del mismo tipo que office_id */}
                                                    {office.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : null}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} xl={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="collect">Seleccionar Cobro</InputLabel>
                                    {collectsOffice && collectsOffice.length > 0 ? (
                                        <Select
                                            label="Seleccionar Cobro"
                                            value={collect_id}
                                            id="collect_id"
                                            labelId="collect_id"
                                            size='small'
                                            onChange={(event) => {
                                                const selectedValue = event.target.value;
                                                setCollect_id(selectedValue);

                                                // Verifica si el valor seleccionado es "Seleccionar Cobro"
                                                if (selectedValue === "Seleccionar Cobro") {
                                                    BoxOfficeMovemente(); // Llama a la función
                                                }
                                            }}
                                            required
                                        >
                                            <MenuItem key="" value="Seleccionar Cobro">
                                                Seleccionar Cobro
                                            </MenuItem>
                                            {collectsOffice.map((collect: Collect) => (
                                                <MenuItem key={collect.id} value={collect.id}>
                                                    {collect.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : null}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} xl={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="date"
                                    label="Fecha inicio"
                                    InputLabelProps={{ shrink: true }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    inputProps={{ max: todayOperational }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} xl={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="date"
                                    label="Fecha fin"
                                    InputLabelProps={{ shrink: true }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    inputProps={{ min: startDate, max: todayOperational }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} xl={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="type-operation-filter-label">Tipo</InputLabel>
                                    <Select
                                        labelId="type-operation-filter-label"
                                        label="Tipo"
                                        value={opFilter}
                                        onChange={(e) => setOpFilter(e.target.value as any)}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="sum">Suma</MenuItem>
                                        <MenuItem value="rest">Resta</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} xl={6} display="flex" alignItems="center" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleApplyDateRange}
                                >
                                    Buscar
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>
                <CardContent
                    sx={{
                        width: (t) => stickyListRegionWidth(t, drawerOpen),
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'auto',
                    }}
                >
                    {sortedRows.length > 0 ? (
                        <SimpleCustomTable
                            rows={sortedRows}
                            columns={simpleColumns}
                            maxHeight={700}
                            rowHeight={24}
                            showBorders
                            showCount
                            exportCsv
                            exportFileName="prestamos-gastos.csv"
                            wrapCellContent
                            columnGroups={[{ header: 'Opciones', fields: ['opt_delete'] }]}
                        />
                    ) : (
                        <EmptyStateMessage message="No existen registros" align="left" />
                    )}
                </CardContent>
            </Card>
        </HeaderResponsive>
    );
};

export default DetalleGastos;
